function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty(target, key, source[key]); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

// flow
import * as React from 'react';
import PropTypes from 'prop-types';
import { css, StyleSheet, color, transition, spacing, breakPoint } from "../../styles/theme";
import { Z_DEPTH_SHADOWS } from "../../constants/sharedConstants";
import { compose, withState, withHandlers } from 'recompose';
var CONFIG = {
  color: color.primaryText,
  backgroundColor: color.white,
  darkThemeColor: color.white,
  darkThemeBackground: color.bgGrayThemeDark,
  roundedBorderRadius: 2
}; // Dynamic styles

function getStyles(_ref) {
  var isCircle = _ref.isCircle,
      isRounded = _ref.isRounded;
  var borderRadiusByIsRounded = isRounded ? CONFIG.roundedBorderRadius : 0;
  return {
    Paper: {
      borderRadius: isCircle ? '50%' : borderRadiusByIsRounded
    }
  };
}

var styles = StyleSheet.create({
  Paper: {
    boxSizing: 'border-box',
    transition: transition.easeOut(),
    position: 'relative'
  },
  xsMinWidth: _defineProperty({}, "@media (max-width: ".concat(breakPoint.xs, "px)"), {
    minWidth: spacing.minWidth
  }),
  fullBleedContainer: {
    width: '100%',
    padding: 0
  },
  lightTheme: {
    color: CONFIG.color,
    backgroundColor: CONFIG.backgroundColor
  },
  darkTheme: {
    color: CONFIG.darkThemeColor,
    backgroundColor: CONFIG.darkThemeBackground
  },
  border: {
    border: "1px solid ".concat(color.divider),
    margin: -1
  },
  transitionNone: {
    transition: 'none'
  },
  boxShadowZDepth0: {
    boxShadow: Z_DEPTH_SHADOWS[0]
  },
  boxShadowZDepth1: {
    boxShadow: Z_DEPTH_SHADOWS[1]
  },
  boxShadowZDepth2: {
    boxShadow: Z_DEPTH_SHADOWS[2]
  },
  boxShadowZDepth3: {
    boxShadow: Z_DEPTH_SHADOWS[3]
  },
  boxShadowZDepth4: {
    boxShadow: Z_DEPTH_SHADOWS[4]
  }
});
/**
 * A generic Paper that accepts children, imgSrc and icon.
 * Sample Usage:
 * <Paper type="primary" size="sm" label={'sm'}/>
 */

function Paper(_ref2) {
  var children = _ref2.children,
      rootClassName = _ref2.rootClassName,
      _ref2$htmlAttributes = _ref2.htmlAttributes,
      htmlAttributes = _ref2$htmlAttributes === void 0 ? {} : _ref2$htmlAttributes,
      style = _ref2.style,
      isThemeDark = _ref2.isThemeDark,
      _refAlt = _ref2._refAlt,
      isCircle = _ref2.isCircle,
      isRounded = _ref2.isRounded,
      isTransitionDisabled = _ref2.isTransitionDisabled,
      zDepthAlt = _ref2.zDepth,
      isInteractive = _ref2.isInteractive,
      isHovered = _ref2.isHovered,
      isFullBleed = _ref2.isFullBleed,
      xsMinWidth = _ref2.xsMinWidth,
      onMouseOver = _ref2.onMouseOver,
      onMouseOut = _ref2.onMouseOut;
  var dynamicStyles = getStyles({
    isCircle: isCircle,
    isRounded: isRounded,
    isThemeDark: isThemeDark
  });

  var mergedStyles = _objectSpread({}, dynamicStyles.Paper, style);

  var interactiveProps = isInteractive ? {
    onMouseOver: onMouseOver,
    onMouseOut: onMouseOut
  } : {};
  var zDepth = isInteractive && isHovered ? zDepthAlt + 1 : zDepthAlt;
  return React.createElement("div", _extends({
    ref: _refAlt
  }, htmlAttributes, css(rootClassName, styles.Paper, isTransitionDisabled && styles.transitionNone, styles["boxShadowZDepth".concat(zDepth)], isThemeDark ? styles.darkTheme : styles.lightTheme, isFullBleed && styles.fullBleedContainer, xsMinWidth && styles.xsMinWidth, isInteractive && styles.isInteractive), {
    style: mergedStyles
  }, interactiveProps), children);
} // Explicity declare the default props for documentation purpose,


Paper.defaultProps = {
  htmlAttributes: {},
  style: {},
  zDepth: 1
};
Paper.propTypes = {
  rootClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // Can use to letters inside the avatar.
  children: PropTypes.node,
  // Props to spread on root element
  htmlAttributes: PropTypes.object,
  // Whether button has dark bg parent element.
  isThemeDark: PropTypes.bool,
  // Whether to enable minWidth in smallest supported screens
  xsMinWidth: PropTypes.bool,
  // Override the inline-styles of the root element.
  style: PropTypes.object,
  _refAlt: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  isCircle: PropTypes.bool,
  isRounded: PropTypes.bool,
  isFullBleed: PropTypes.bool,
  isTransitionDisabled: PropTypes.bool,
  // zDepth of the paper shadow, -1 will not only show shadow and no border
  zDepth: PropTypes.oneOf([-1, 0, 1, 2, 3, 4]),

  /* Below props are soly used for interactivity, when hovered, we'll add one to zDepth */
  // Whether to show hover state
  isInteractive: PropTypes.bool,
  isHovered: PropTypes.bool,
  onMouseOver: PropTypes.func,
  onMouseOut: PropTypes.func
};
export default compose(withState('isHovered', 'toggleHover', false), withHandlers({
  onMouseOver: function onMouseOver(props) {
    return function () {
      return props.toggleHover(true);
    };
  },
  onMouseOut: function onMouseOut(props) {
    return function () {
      return props.toggleHover(false);
    };
  }
}))(Paper);